import {Component, ElementRef, Injectable, OnInit, ViewChild} from '@angular/core';
import {MachinesService} from '../../services/machines.service';
import {TreeNode} from 'primeng/api';
import {CommonMessagesService} from '../../services/common-messages.service';
import {Machine} from '../../model/machine';
import {MachineFilters} from '../../model/filters/machine-filters';
import {HierarchyService} from '../../services/hierarchy.service';
import {MachineModelsService} from '../../services/machine-models.service';
import {MachineGroupsService} from '../../services/machine-groups.service';
import {MachineModel} from '../../model/machine-model';
import {MachineGroup} from '../../model/machine-group';
import {Geocoding} from '../../model/geocoding';
import {GeocodingDataResponse, GeocodingService} from '../../services/geocoding.service';
import {ColorService} from '../../services/color.service';
import {AuthGuard} from '../../common/auth-guard.service';
import {AlarmsService} from 'src/app/services/alarms.service';
import {AlarmDefinitionSummary} from 'src/app/model/alarm-definition-summary';
import {TimezoneService} from '../../services/timezone.service';
import {ExportFileType} from '../../model/export-file-type';
import {TranslocoService} from '@ngneat/transloco';

@Component({
  selector: 'app-landing',
  templateUrl: './machine-list.component.html',
  styleUrls: ['./machine-list.component.scss']
})
@Injectable({
  providedIn: 'root'
})
export class MachineListComponent implements OnInit {

  @ViewChild('fileUploader', {static: false})
  fileInput: ElementRef;

  public language: string;

  public machines: Machine[];
  public totalMachines: number;
  public loadingMachines: boolean;

  public rows: number = 10;
  public first: number = 0;

  public filtersVisible: boolean;
  public exportVisible: boolean;
  public currentFilters: MachineFilters;
  public deleted: boolean = false;

  public hierarchy: TreeNode[];
  public organizationCodes: any[];
  public organizations: any[];

  public models: MachineModel[];
  public countries: { name: string }[];
  public communicationTypes: any[];
  public groups: MachineGroup[];
  public alarms: AlarmDefinitionSummary[];

  public isStateDisabled: boolean;
  public isLocalityDisabled: boolean;

  public availableYearRange: string;
  public rangeDates: Date[];

  private states: { name: string }[];
  private localities: { name: string }[];
  private geocodings: Geocoding[];
  private countryStates: Map<string, Set<string>>;
  private stateLocalities: Map<string, Set<string>>;


  constructor(private machinesService: MachinesService,
              private geocodingService: GeocodingService,
              public authGuard: AuthGuard,
              private commonMessagesService: CommonMessagesService,
              private hierarchyService: HierarchyService,
              private machineModelsService: MachineModelsService,
              private machineGroupsService: MachineGroupsService,
              private colorService: ColorService,
              private readonly i18n: TranslocoService,
              private readonly alarmsService: AlarmsService,
              private readonly timezoneService: TimezoneService) {
    this.currentFilters = new MachineFilters();
    this.currentFilters.deleted = false;
    this.availableYearRange = this.timezoneService.calcYearRange();
    this.communicationTypes = [{label: 'GPRS', value: 0}, {label: 'WIFI', value: 1}];
  }

  ngOnInit(): void {
    this.machines = new Array(this.rows);
    this.language = this.i18n.getActiveLang();
    this.loadFilters();
  }

  confirmAndDelete(machine: Machine): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteMachine(machine.machineUuid);
    });
  }

  deleteMachine(machineUuid: string): void {
    this.machinesService.deleteMachine(machineUuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadMachines(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  private loadMachines(offset?: number, limit?: number): void {
    this.loadingMachines = true;
    this.machines = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.machinesService.getMachines(offset, limit, this.currentFilters).subscribe(response => {
      this.machines = response.data.content;
      this.totalMachines = response.data.totalElements;
      this.loadingMachines = false;
    });
  }

  trimFieldsForm(filters: MachineFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  onPageChange(event): void {
    this.loadMachines(event.first / event.rows, event.rows);
  }

  private loadFilters(): void {
    this.buildTreeNode();
    this.machineModelsService.getModels().subscribe(response => {
      this.models = response.models;
    });
    this.loadGeocoding();

    this.groups = [];
    this.alarms = [];

    if (this.authGuard.hasPermission('PERMISSION_MANAGE_ADVANCED_MACHINES')) {
      this.machineGroupsService.getGroupsSummary().subscribe(response => {
        this.groups = response.machineGroups;
      });
    }
    if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
      this.alarmsService.getAlarmsDefinitionFilters().subscribe(response => {
        this.alarms = response.alarmsDefinition;
      });
    }
  }

  private loadGeocoding(): void {
    this.isStateDisabled = true;
    this.isLocalityDisabled = true;
    this.countryStates = new Map<string, Set<string>>();
    this.stateLocalities = new Map<string, Set<string>>();
    this.geocodingService.getMachineGeocodings(this.language).subscribe(response => {
      this.geocodings = response.geocodings;
      let geocodingData: GeocodingDataResponse = this.geocodingService.getGeocodingData(this.geocodings, this.language);
      this.countryStates = geocodingData.countryStates;
      this.stateLocalities = geocodingData.stateLocalities;
      this.countries = geocodingData.countries;
      this.states = geocodingData.states;
      this.localities = geocodingData.localities;
    });
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      let responseHierarchy: any[] = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto);
      this.hierarchy = responseHierarchy[1];
      this.organizationCodes = responseHierarchy[2].sort((a, b) => a.label > b.label ? 1 : -1);
    });
  }

  public mapProperty(array: any[], property: string): any[] {
    return array.map(i => i[property]);
  }

  onCountryChange(event): void {
    this.isStateDisabled = !(event.value && event.value.length > 0);
    this.isLocalityDisabled = true;
    this.currentFilters.states = [];
    this.currentFilters.localities = [];
  }

  onStateChange(event): void {
    this.isLocalityDisabled = !(event.value && event.value.length > 0);
    this.currentFilters.localities = [];
  }

  get filteredStates(): { name: string }[] {
    if (this.currentFilters.countries?.length > 0) {
      return this.states.filter(state => {
        for (const country of this.currentFilters.countries) {
          if (this.countryStates.get(country).has(state.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.states;
  }

  get filteredLocalities(): { name: string }[] {
    if (this.currentFilters.states?.length > 0) {
      return this.localities.filter(locality => {
        for (const state of this.currentFilters.states) {
          if (this.stateLocalities.get(state).has(locality.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.localities;
  }

  resetFilters(): void {
    this.currentFilters = new MachineFilters();
    this.deleted = false;
    this.currentFilters.deleted = false;
    this.organizations = [];
    this.rangeDates = undefined;
  }

  applyFilters(): void {
    this.first = 0;
    this.deleted = this.currentFilters.deleted;
    this.currentFilters.organizationUuids = [];
    if (this.organizations != null && this.organizations.length > 0) {
      this.organizations.forEach(organization => {
        this.currentFilters.organizationUuids.push(organization.data);
      });
    }
    if (this.rangeDates) {
      let periodRange: { from: number, until: number };
      this.currentFilters.from = this.rangeDates[0].getTime();
      if (this.rangeDates.length == 1 || this.rangeDates[1] == null) {
        periodRange = this.timezoneService.timestampRangeFromCalendarData([this.rangeDates[0], this.rangeDates[0]]);
      } else {
        periodRange = this.timezoneService.timestampRangeFromCalendarData([this.rangeDates[0], this.rangeDates[1]]);
      }
      this.currentFilters.from = periodRange.from;
      this.currentFilters.until = periodRange.until;
    } else if (this.currentFilters.from) {
      this.currentFilters.from = null;
      this.currentFilters.until = null;
    }
    this.loadMachines(this.first / this.rows, this.rows);
  }

  adjustTextColor(colorHex): string {
    return this.colorService.adjustTextColor(colorHex);
  }

  public readonly ExportFileType = ExportFileType;
}
